import { graphql, useStaticQuery } from 'gatsby'
import React, { memo, useCallback, useRef, useState } from 'react'

import reviewVideo from '../images/reviews/Jabrille_Lotus_Trading.mp4'

const MuteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
  </svg>
)

const UnmuteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
)

const News = ({ isAbout }) => {
  const queryData = useStaticQuery(graphql`
    query NewsQuery {
      Bigjerryfencing: file(relativePath: { eq: "news/Bigjerryfencing.jpg" }) {
        childImageSharp { fluid { src } }
      }
      yazoPOS1: file(relativePath: { eq: "news/yazoPOS1.png" }) {
        childImageSharp { fluid { src } }
      }
      nurtureful: file(relativePath: { eq: "caseStudies/nurturefulth.png" }) {
        childImageSharp { fluid { src } }
      }
      muscatMMG: file(relativePath: { eq: "caseStudies/mediamuscatgroup.png" }) {
        childImageSharp { fluid { src } }
      }
      JabrilleLotus: file(relativePath: { eq: "reviews/Jabrille_Lotus_Trading.png" }) {
        childImageSharp { fluid { src } }
      }
      orangeline: file(relativePath: { eq: "caseStudies/orangelinetrain.png" }) {
        childImageSharp { fluid { src } }
      }
      eme: file(relativePath: { eq: "caseStudies/eme_story.png" }) {
        childImageSharp { fluid { src } }
      }
    }
  `)

  const items = [
    {
      type: 'card',
      image: queryData.JabrilleLotus?.childImageSharp?.fluid?.src,
      badge: 'Client Story',
      title: 'Jabrille Lotus Trading — See how they transformed their business with Visiomate',
      // cta: 'Read now',
      // href: '/caseStudies/projects/muscatMediaGroup',
    },
    // {
    //   type: 'video',
    //   badge: 'Client Story',
    //   title: 'Jabrille Lotus Trading — See how they transformed their business with Visiomate',
    // },
    {
      type: 'card',
      image: queryData.muscatMMG?.childImageSharp?.fluid?.src,
      badge: 'Customer Story',
      title: 'Automated HR Management System for Muscat Media Group — Oman\'s leading media conglomerate',
      cta: 'Read now',
      href: '/caseStudies/projects/muscatMediaGroup',
    },
    {
      type: 'card',
      image: queryData.eme?.childImageSharp?.fluid?.src,
      bgStyle: { background: 'linear-gradient(135deg, #0a3d6b 0%, #1a6b8a 50%, #0d4a3e 100%)' },
      badge: 'Customer Story',
      title: 'Procurement Security & Multi-Currency Finance for EME Aquatic Systems — Abu Dhabi, UAE',
      cta: 'Read now',
      href: '/caseStudies/projects/emeAquaticSystems',
    },
    {
      type: 'card',
      image: queryData.orangeline?.childImageSharp?.fluid?.src,
      badge: 'Customer Story',
      title: 'Construction Management Solution for Orangeline Pakistan — from planning to handover',
      cta: 'Read now',
      href: '/caseStudies/projects/orangelinePakistan',
    },
    {
      type: 'card',
      image: queryData.Bigjerryfencing?.childImageSharp?.fluid?.src,
      badge: 'Customer Story',
      title: "Complete Digital Transformation for Big Jerry's Fencing from lead intake to delivery",
      cta: 'Read now',
      href: '/caseStudies/projects/bigJerrysFencing',
    },
    {
      type: 'card',
      image: queryData.nurtureful?.childImageSharp?.fluid?.src,
      badge: 'Customer Story',
      title: 'End-to-End Digital Transformation for Nurtureful Psychology',
      cta: 'Read now',
      href: '/caseStudies/projects/nurturefulPsychology',
    },
  ]

  const PER_PAGE = 3
  const pageCount = Math.ceil(items.length / PER_PAGE)

  const [page, setPage] = useState(0)
  const [muted, setMuted] = useState(true)
  const videoRef = useRef(null)

  const prev = useCallback(() => setPage(p => (p - 1 + pageCount) % pageCount), [pageCount])
  const next = useCallback(() => setPage(p => (p + 1) % pageCount), [pageCount])

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setMuted(m => !m)
    }
  }

  const visible = items.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  return (
    <section className="ns-section">
      {/* Header */}
      <div className="ns-header">
        <span className="ns-eyebrow">
          {isAbout ? 'Want to Discover More?' : 'Customer Stories'}
        </span>
        <h2 className="ns-heading">
          {isAbout
            ? "See what we've built for our clients"
            : 'Real projects. Real outcomes. Real clients.'}
        </h2>
      </div>

      {/* Unified carousel grid */}
      <div className="ns-grid">
        {visible.map((item, i) =>
          item.type === 'video' ? (
            <div key={i} className="ns-card ns-card--video">
              <video
                ref={videoRef}
                className="ns-card__video"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={reviewVideo} type="video/mp4" />
              </video>
              <span className="ns-card__badge">{item.badge}</span>
              <div className="ns-card__footer">
                <p className="ns-card__title">{item.title}</p>
                <button
                  className={`ns-mute-btn${muted ? ' ns-mute-btn--muted' : ''}`}
                  onClick={toggleMute}
                  aria-label={muted ? 'Unmute video' : 'Mute video'}
                >
                  {muted ? <MuteIcon /> : <UnmuteIcon />}
                  <span>{muted ? 'Unmute' : 'Mute'}</span>
                </button>
              </div>
            </div>
          ) : (
            <a
              key={i}
              href={item.href}
              className="ns-card"
              style={item.image
                ? { backgroundImage: `url(${item.image})` }
                : item.bgStyle || {}
              }
              target={item.href?.startsWith('http') ? '_blank' : undefined}
              rel={item.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              <span className="ns-card__badge">{item.badge}</span>
              <div className="ns-card__footer">
                <p className="ns-card__title">{item.title}</p>
                {item.cta && <span className="ns-card__cta">{item.cta}</span>}
              </div>
            </a>
          )
        )}
      </div>

      {/* Navigation */}
      <div className="ns-nav">
        <button className="ns-nav__arrow" onClick={prev} aria-label="Previous">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="ns-nav__dots">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              className={`ns-nav__dot${i === page ? ' ns-nav__dot--active' : ''}`}
              onClick={() => setPage(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
        <button className="ns-nav__arrow" onClick={next} aria-label="Next">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  )
}

export default memo(News)
